import dayjs, { Dayjs } from "dayjs";

/**
 * Shared date-range validation for Admin table filters.
 *
 * Rules enforced across the panel:
 *  - No date may be in the future.
 *  - "From Date" must be earlier than or equal to "To Date".
 *
 * An open-ended range (only From, or only To) is allowed — we only validate
 * the constraints that actually apply to the provided values.
 *
 * NOTE: these rules are for FILTER date ranges (which look at historical data).
 * They must NOT be applied to form date fields where future dates are legitimate
 * (e.g. coupon validity, service-fee effective dates).
 */

export interface DateRangeValidation {
  valid: boolean;
  message?: string;
}

/** Today's date in local time as an ISO `YYYY-MM-DD` string (for native `type="date"` inputs). */
export const todayISO = (): string => dayjs().format("YYYY-MM-DD");

/**
 * Canonical display format for a single date shown in the UI (e.g. applied-filter
 * chips): always `YYYY-MM-DD` (like `2026-07-11`). Accepts a string, Date, or Dayjs.
 * Returns "" for empty/invalid values.
 */
export const formatDisplayDate = (
  value?: string | number | Date | Dayjs | null
): string => {
  if (value === null || value === undefined || value === "") return "";
  const d = dayjs(value as any);
  return d.isValid() ? d.format("YYYY-MM-DD") : "";
};

/**
 * Validate a `YYYY-MM-DD` string range (native `type="date"` inputs).
 * String comparison is safe because ISO `YYYY-MM-DD` sorts lexicographically.
 */
export const validateDateRange = (
  start?: string,
  end?: string
): DateRangeValidation => {
  const today = todayISO();

  if (start && start > today) {
    return { valid: false, message: "From Date cannot be in the future" };
  }
  if (end && end > today) {
    return { valid: false, message: "To Date cannot be in the future" };
  }
  if (start && end && start > end) {
    return {
      valid: false,
      message: "From Date must be earlier than or equal to To Date",
    };
  }
  return { valid: true };
};

/**
 * Validate a Dayjs range (MUI `DateRangePicker` value: `[start, end]`).
 * Comparisons are done on day boundaries so a "today" selection is always valid.
 */
export const validateDayjsRange = (
  range?: [Dayjs | null, Dayjs | null]
): DateRangeValidation => {
  const [start, end] = range || [null, null];
  const endOfToday = dayjs().endOf("day");

  if (start && start.isAfter(endOfToday)) {
    return { valid: false, message: "From Date cannot be in the future" };
  }
  if (end && end.isAfter(endOfToday)) {
    return { valid: false, message: "To Date cannot be in the future" };
  }
  if (start && end && start.startOf("day").isAfter(end.startOf("day"))) {
    return {
      valid: false,
      message: "From Date must be earlier than or equal to To Date",
    };
  }
  return { valid: true };
};
